<template>
  <div>
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>操作履歴一覧</span>
        <manual-button path="/nwinfo-ope/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search
              :search="history.search"
              v-model="history.filter"
              :initial-form="
                () => ({
                  acceptDateTime: { start: null, end: null },
                  endDateTime: { start: null, end: null },
                })
              "
            >
              <template #input(custom)="{ form, option }">
                <v-row class="align-center justify-center">
                  <template v-if="option.key === 'acceptDateTime'">
                    <!-- 受付日時の期間検索 -->
                    <v-col>
                      <validation-provider
                        name="acceptDateTimeStart"
                        label="開始"
                        v-model="form['acceptDateTime']['start']"
                        :rules="{
                          required: true,
                          date_format: 'YYYY-MM-DDTHH:mm',
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          :hide-details="true"
                          type="datetime-local"
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        ></app-form-input>
                      </validation-provider>
                    </v-col>
                    <span class="mx-2">〜</span>
                    <v-col>
                      <validation-provider
                        name="acceptDateTimeEnd"
                        label="終了"
                        v-model="form['acceptDateTime']['end']"
                        :rules="{
                          required: true,
                          date_format: 'YYYY-MM-DDTHH:mm',
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          :hide-details="true"
                          type="datetime-local"
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        ></app-form-input>
                      </validation-provider>
                    </v-col>
                  </template>
                  <template v-else>
                    <!-- 完了日時の期間検索 -->
                    <v-col>
                      <validation-provider
                        name="endDateTimeStart"
                        label="開始"
                        v-model="form['endDateTime']['start']"
                        :rules="{
                          required: true,
                          date_format: 'YYYY-MM-DDTHH:mm',
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          :hide-details="true"
                          type="datetime-local"
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        ></app-form-input>
                      </validation-provider>
                    </v-col>
                    <span class="mx-2">〜</span>
                    <v-col>
                      <validation-provider
                        name="endDateTimeEnd"
                        label="終了"
                        v-model="form['endDateTime']['end']"
                        :rules="{
                          required: true,
                          date_format: 'YYYY-MM-DDTHH:mm',
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-form-input
                          :hide-details="true"
                          type="datetime-local"
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        ></app-form-input>
                      </validation-provider>
                    </v-col>
                  </template>
                  <v-btn icon="$mdiMagnify" type="submit"></v-btn>
                </v-row>
              </template>

              <!-- 受付日時のタグ表示 -->
              <template #tagValue(acceptDateTime)="{ value }">
                {{ $filter.datetime(value.start) }} ~
                {{ $filter.datetime(value.end) }}
              </template>

              <!-- 完了日時のタグ表示 -->
              <template #tagValue(endDateTime)="{ value }">
                {{ $filter.datetime(value.start) }} ~
                {{ $filter.datetime(value.end) }}
              </template>
            </app-search>
          </v-col>
          <div class="d-flex flex-row ga-1 flex-wrap ml-2">
            <v-btn
              :href="$api.operationHistory.getDownloadUrl()"
              download
              color="primary"
              >一覧ダウンロード</v-btn
            >
          </div>
        </v-row>

        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="history.sortBy"
          v-model:expanded="expandedRows"
          primary-key="operationSeq"
          :fields="history.fields"
          :items="operationHistory.operationList"
          :per-page="30"
          :filter="history.filter"
          :filter-option="history.filter_option"
          :td-class="cellClass"
          sticky-header="noSticky"
        >
          <template #cell(account)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <template #cell(acceptDateTime)="{ value }">
            {{ $filter.datetime(value) }}
          </template>
          <template #cell(endDateTime)="{ value }">
            {{ $filter.datetime(value) }}
          </template>
          <template #cell(note)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
          <template #cell(orderStatus)="{ item }">
            <v-chip :color="getStatusColor(item.orderStatus)">
              {{ $filter.enumTo(item.orderStatus, "operationOrderStatus") }}
            </v-chip>
          </template>
          <template #cell(button)="row">
            <v-btn
              v-if="!showDetailButton(row.item)"
              color="primary"
              :variant="row.isExpanded(row.internalItem) ? 'outlined' : 'flat'"
              @click="
                () => {
                  historyDetails(row.item.operationSeq);
                  row.toggleExpand(row.internalItem);
                }
              "
              >{{ row.isExpanded(row.internalItem) ? "閉じる" : "詳細" }}</v-btn
            >
          </template>
          <template v-slot:expanded-row="{ item }">
            <div
              v-for="order in detailsMap.get(item.operationSeq)"
              :key="order.orderType"
              class="mb-4 mx-4"
            >
              <v-row class="mt-2">
                <v-col>
                  <span class="font-weight-bold">{{ order.orderType }}</span>
                </v-col>
              </v-row>
              <v-card class="mt-2">
                <v-card-text
                  class="d-flex pa-2 ma-4 bg-grey-lighten-4 rounded-xl"
                >
                  <v-row
                    class="align-center pl-3"
                    no-gutters
                    justify="space-between"
                  >
                    <v-col>
                      <div>
                        {{ order.orderTarget }}
                      </div>
                      <div class="text-caption text-pre-wrap">
                        {{ order.additionalInformation }}
                      </div>
                    </v-col>
                    <v-chip :color="getStatusColor(order.orderStatus)">
                      {{
                        $filter.enumTo(
                          order.orderStatus,
                          "operationOrderStatus"
                        )
                      }}
                    </v-chip>
                  </v-row>
                </v-card-text>
                <v-card-text class="pt-0">
                  <v-table density="compact" class="table-bordered">
                    <thead>
                      <tr>
                        <th class="text-center font-weight-bold item-column">
                          項目
                        </th>
                        <th class="text-center font-weight-bold column-min">
                          変更前
                        </th>
                        <th class="text-center font-weight-bold column-min">
                          変更後
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(detail, index) in order.orderDetailList"
                        :key="index"
                      >
                        <td>{{ detail.orderItem }}</td>
                        <td class="bg-pink-lighten-5">
                          {{ $filter.emptyTo(detail.before, "-") }}
                        </td>
                        <td class="bg-light-green-lighten-5">
                          {{ $filter.emptyTo(detail.after, "-") }}
                        </td>
                      </tr>
                      <tr v-if="!order.orderDetailList.length">
                        <td colspan="3" class="text-center">
                          該当データがありません。
                        </td>
                      </tr>
                    </tbody>
                  </v-table>
                </v-card-text>
              </v-card>
            </div>
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import AppFormInput from "@/components/AppFormInput.vue";
import type {
  OperationHistoryEntity,
  OperationListEntity,
} from "@/apis/OperationHistoryApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { OrderListEntity } from "@/apis/OperationHistoryApi";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "OperationHistoryList",
  components: { AppFormInput },
  data() {
    return {
      history: {
        sortBy: {
          key: "acceptDateTime",
          order: "desc",
        } as SortItem,
        fields: [
          { key: "content", title: "内容", width: "250px", sortable: true },
          {
            key: "account",
            title: "実行アカウント",
            width: "250px",
            sortable: true,
          },
          {
            key: "acceptDateTime",
            title: "受付日時",
            width: "200px",
            sortable: true,
          },
          {
            key: "endDateTime",
            title: "完了日時",
            width: "200px",
            sortable: true,
          },
          {
            key: "orderStatus",
            title: "ステータス",
            width: "170px",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "operationOrderStatus"),
          },
          {
            key: "note",
            title: "オーダー備考",
            width: "300px",
            sortable: true,
          },
          {
            key: "button",
            title: "",
            width: "150px",
          },
        ] as AppTableHeader<OperationListEntity>[],
        items: [],
        search: {
          content: {
            type: "select",
            label: "内容",
            items: [
              "ポリシー設定",
              "サービス申込",
              "契約取込",
              "手動契約取込",
              "収容変更",
              "インターネット一時停止",
              "仮想NW申込",
              "VNコネクト設定",
              "フロー設定",
              "VN回線有効無効設定",
              "インターフェース設定",
              "サービス申込(Type3)",
              "アクセス品目申込",
              "IPsec設定",
              "エクストラネット接続申込",
              "Type3ポリシー設定",
              "Type3経路設定",
              "ポリシー設定(プラットフォームゲートウェイ)",
              "VN回線有効無効設定（収容変更）",
              "フロー設定（収容変更）",
              "VN回線設定（収容変更）",
              "インターフェース設定（収容変更）",
              "VN回線収容変更",
              "Hub&Spoke接続申込",
              "VN L2/L3コネクト設定",
              "トラフィック情報取得開始",
              "VNコネクト一括設定",
              "クラウド毎帯域設定",
              "サービスタグ取得",
              "サービス申込(Microsoftサービス)",
              "クラウド向けVNコネクト設定",
              "クラウド向けVN回線有効無効設定",
              "クラウド向けインターフェース設定",
              "サービス申込(Type4)",
              "Type4ポリシー設定",
              "Type4経路設定",
              "CPA 5G SA 契約変更",
              "CPA 5G SA NW設定",
              "CPA 5G SA 利用開始",
              "CPA 5G SA 認証ID一括登録",
              "サービス申込(SaaS)",
              "SASEゲートウェイVNコネクト設定",
              "SASEゲートウェイVNコネクト有効無効設定",
              "SASEゲートウェイ向けインターフェース設定",
            ].map((e) => ({ value: e, text: e })),
          },
          account: { type: "text", label: "実行アカウント" },
          acceptDateTime: { type: "custom", label: "受付日時" },
          endDateTime: { type: "custom", label: "完了日時" },
          orderStatus: {
            type: "select",
            label: "ステータス",
            items: [
              { value: "SUCCESS", text: "成功" },
              { value: "FAILURE", text: "失敗" },
              { value: "RESERVATION", text: "予約中" },
              { value: "SETTING", text: "設定中" },
              { value: "ACCEPTED", text: "受付完了、処理待ち" },
            ],
          },
          note: { type: "text", label: "オーダー備考" },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          content: "equal",
          account: "like",
          acceptDateTime: (
            item: OperationListEntity,
            value: { start: string; end: string }
          ) => {
            return this.$moment(item.acceptDateTime).isBetween(
              this.$moment(value.start),
              this.$moment(value.end),
              undefined,
              "[]"
            );
          },
          endDateTime: (
            item: OperationListEntity,
            value: { start: string; end: string }
          ) => {
            return this.$moment(item.endDateTime).isBetween(
              this.$moment(value.start),
              this.$moment(value.end),
              undefined,
              "[]"
            );
          },
          orderStatus: "equal",
          note: "like",
        } as FilterOption,
      },
      operationHistory: {} as OperationHistoryEntity,
      isLoaded: false,
      noDetailContent: new Set([
        "契約取込",
        "手動契約取込",
        "エクストラネット接続申込",
        "VN回線収容変更",
        "Hub&Spoke接続申込",
        "トラフィック情報取得開始",
        "VNコネクト一括設定",
      ]),
      detailsMap: new Map<string, OrderListEntity[]>(),
      expandedRows: [],
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.operationHistory = await this.$api.operationHistory.getAll();
      this.expandedRows = [];
      this.detailsMap.clear();
    },

    /** 詳細がないまたは2016/12/21以前の場合、詳細ボタンを非表示 */
    showDetailButton(item: OperationListEntity): boolean {
      return (
        this.noDetailContent.has(item.content) ||
        this.$moment(item.acceptDateTime).format("YYYY-MM-DD") <= "2016-12-21"
      );
    },

    /** 操作履歴情報画面を表示*/
    async historyDetails(operationSeq: string) {
      if (!this.detailsMap.has(operationSeq)) {
        const response =
          await this.$api.operationHistory.getReferenceList(operationSeq);
        this.detailsMap.set(operationSeq, response.orderList);
      }
    },

    /** tdにクラスを追加したい場合、ここで */
    cellClass(
      field: AppTableHeader<OperationListEntity>
    ): string[] | undefined {
      if (field.key === "note") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },

    /** ステータスに色を設定する */
    getStatusColor(status: string): string {
      const colorMap: Record<string, string> = {
        SUCCESS: "success",
        FAILURE: "error",
        RESERVATION: "default",
        SETTING: "warning",
        ACCEPTED: "info",
      };
      return colorMap[status];
    },
  },
});
</script>

<style lang="scss" scoped>
.item-column {
  width: 273px;
}

:deep(.v-data-table__tr) {
  cursor: default !important;
}

.column-min {
  min-width: 150px;
}
</style>
